import { useEffect, useRef } from "react";
import { useRecoilState } from "recoil";

import { NewNavOptInState } from "MVVM/State/nav";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";

// Module scope, not a ref: the param should be consumed once per page load no
// matter how many components call this hook.
let paramApplied = false;

export const resetNewNavParamForTests = () => {
  paramApplied = false;
};

export const useNewNav = () => {
  const [enabled, setEnabled] = useRecoilState(NewNavOptInState);
  const { track } = useAnalyticsContext();
  const isInternal = useIsInternalAccount();
  const forced = useRef(false);

  useEffect(() => {
    if (paramApplied) return;
    paramApplied = true;

    const param = getQueryParam("newnav");
    if (param !== "1" && param !== "0") return;

    const next = param === "1";
    if (next === enabled) return;

    setEnabled(next);
    track("Nav Variant Changed", {
      nav_variant: next ? "new_nav" : "control",
      source: "query_param",
    });
    // Runs once. Re-running on `enabled` would re-fire the exposure event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Internal accounts are opted in automatically. This writes the flag rather
   * than just returning true, so useAnalyticsContext reports them as new_nav
   * instead of tagging them control while they look at the new bar.
   *
   * ?newnav=0 still wins: without that escape hatch nobody internal could QA
   * the control cohort. Not latched behind a module guard because isInternal is
   * false until the user hydrates.
   */
  useEffect(() => {
    if (forced.current || !isInternal || enabled) return;
    if (getQueryParam("newnav") === "0") return;

    forced.current = true;
    setEnabled(true);
    track("Nav Variant Changed", {
      nav_variant: "new_nav",
      source: "internal_account",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternal, enabled]);

  // ponytail: kill switch, so ending the experiment does not require clearing
  // every opted-in user's localStorage.
  const newNav = enabled && process.env.REACT_APP_NEW_NAV_KILL !== "true";

  return { newNav, setNewNav: setEnabled };
};
